// src/pages/ELearning/AnalyticsPage.tsx — DESIGN v2
import React, { useEffect, useState } from 'react';
import { getAnalytics } from '../../api/client';
import { Loader2, TrendingUp, Clock, Award, CheckCircle, Download, Target, Zap } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const GRADIENTS = {
  blue: 'linear-gradient(135deg, #2575fc 0%, #6a11cb 100%)',
  green: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  orange: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
};

const KPI_COLORS = {
  green: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
  blue: { color: '#2575fc', bg: 'rgba(37, 117, 252, 0.12)' },
  purple: { color: '#667eea', bg: 'rgba(102, 126, 234, 0.12)' },
  orange: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
};

const AnalyticsPage: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token')
                 || localStorage.getItem('accessToken')
                 || localStorage.getItem('authToken');
      const res = await fetch('/api/elearning/results/export.xlsx', {
        headers: token ? { Authorization: 'Bearer ' + token } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert('Ошибка: ' + (err.error || res.status));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'elearning-results-' + new Date().toISOString().slice(0, 10) + '.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error:', e);
      const msg = e instanceof Error ? e.message : String(e);
      alert('Ошибка экспорта: ' + msg);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await getAnalytics();
        setData(res);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Loader2 size={40} className="spin" color="#667eea" />
        </div>
      </>
    );
  }

  if (!data) {
    return <><p style={{ color: '#5a5f7a' }}>Нет данных</p></>;
  }

  const completed = data.stats?.completed || 0;
  const avgScore = data.stats?.avgScore || 0;
  const currentRating = data.currentRating || 0;
  const totalTime = data.stats?.totalTime || 0;
  const streak = data.stats?.streak || 0;

  return (
    <>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes countUp {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .an-kpi {
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          animation: fadeUp 0.5s ease-out backwards;
        }
        .an-kpi:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 50px rgba(31, 38, 135, 0.15) !important;
        }
        .an-kpi:nth-child(1) { animation-delay: 0.05s; }
        .an-kpi:nth-child(2) { animation-delay: 0.1s; }
        .an-kpi:nth-child(3) { animation-delay: 0.15s; }
        .an-kpi:nth-child(4) { animation-delay: 0.2s; }
        .an-export-btn {
          transition: all 0.3s ease;
        }
        .an-export-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(17, 153, 142, 0.4) !important;
        }
      `}</style>

      {/* ═══ Hero + Export ═══ */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 32,
        flexWrap: 'wrap',
        gap: 16,
        animation: 'fadeUp 0.4s ease-out',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: 20,
            background: GRADIENTS.purple,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
          }}>
            <TrendingUp size={32} color="#fff" />
          </div>
          <div>
            <h1 style={{
              fontSize: 32,
              fontWeight: 800,
              margin: 0,
              background: 'linear-gradient(135deg, #1a1a2e 0%, #667eea 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.5px',
            }}>
              Моя аналитика
            </h1>
            <p style={{ color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 }}>
              Прогресс обучения и результаты
            </p>
          </div>
        </div>

        <button
          onClick={handleExportExcel}
          disabled={exporting}
          className="an-export-btn"
          style={{
            padding: '12px 24px',
            background: exporting ? '#95a5a6' : GRADIENTS.green,
            color: '#fff',
            border: 'none',
            borderRadius: 14,
            fontSize: 14,
            fontWeight: 700,
            cursor: exporting ? 'wait' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 8px 24px rgba(17, 153, 142, 0.3)',
          }}
          title="Скачать все результаты тестов в Excel"
        >
          <Download size={16} />
          {exporting ? 'Экспорт...' : 'Экспорт в Excel'}
        </button>
      </div>

      {/* ═══ KPI СЕТКА ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        marginBottom: 32,
      }}>
        <KPI
          icon={<CheckCircle size={22} color={KPI_COLORS.green.color} />}
          value={completed}
          label="Завершено тестов"
          color={KPI_COLORS.green}
          accent={GRADIENTS.green}
        />
        <KPI
          icon={<Award size={22} color={KPI_COLORS.orange.color} />}
          value={currentRating}
          label="Текущий рейтинг"
          color={KPI_COLORS.orange}
          accent={GRADIENTS.orange}
        />
        <KPI
          icon={<Target size={22} color={KPI_COLORS.blue.color} />}
          value={`${avgScore}%`}
          label="Средний балл"
          color={KPI_COLORS.blue}
          accent={GRADIENTS.blue}
        />
        <KPI
          icon={<Clock size={22} color={KPI_COLORS.purple.color} />}
          value={`${totalTime} мин`}
          label="Общее время"
          color={KPI_COLORS.purple}
          accent={GRADIENTS.purple}
        />
      </div>

      {/* ═══ ГРАФИК ═══ */}
      {data.ratingHistory?.length > 0 && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.55)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.7)',
          borderRadius: 28,
          padding: 28,
          boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)',
          animation: 'fadeUp 0.6s ease-out 0.25s backwards',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: GRADIENTS.blue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Zap size={20} color="#fff" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
                Динамика рейтинга
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8b8fa8' }}>
                Последние {data.ratingHistory.length} записей
              </p>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.ratingHistory}>
              <defs>
                <linearGradient id="gradRating" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#667eea" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#764ba2" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="strokeRating" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#2575fc" />
                  <stop offset="100%" stopColor="#6a11cb" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(102, 126, 234, 0.08)" />
              <XAxis
                dataKey="date"
                stroke="#8b8fa8"
                style={{ fontSize: 12 }}
                tickLine={false}
              />
              <YAxis
                stroke="#8b8fa8"
                style={{ fontSize: 12 }}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: 12,
                  boxShadow: '0 8px 24px rgba(31, 38, 135, 0.12)',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              />
              <Area
                type="monotone"
                dataKey="rating"
                stroke="url(#strokeRating)"
                strokeWidth={3}
                fill="url(#gradRating)"
                dot={{ fill: '#667eea', strokeWidth: 2, r: 4, stroke: '#fff' }}
                activeDot={{ r: 6, fill: '#2575fc', stroke: '#fff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ═══ Пустое состояние графика ═══ */}
      {(!data.ratingHistory || data.ratingHistory.length === 0) && (
        <div style={{
          textAlign: 'center',
          padding: 60,
          background: 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(20px)',
          borderRadius: 24,
          border: '1px dashed rgba(102, 126, 234, 0.3)',
        }}>
          <TrendingUp size={48} color="#667eea" style={{ opacity: 0.4 }} />
          <h3 style={{ color: '#5a5f7a', marginTop: 16, fontWeight: 600 }}>
            Пока нет истории
          </h3>
          <p style={{ color: '#8b8fa8', fontSize: 14 }}>
            Пройдите тесты, чтобы увидеть динамику
          </p>
        </div>
      )}
    </>
  );
};

// ═══ KPI КАРТОЧКА ═══
const KPI: React.FC<{
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  color: { color: string; bg: string };
  accent: string;
}> = ({ icon, value, label, color, accent }) => (
  <div
    className="an-kpi"
    style={{
      background: 'rgba(255, 255, 255, 0.55)',
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.7)',
      borderRadius: 24,
      padding: 24,
      boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: accent,
    }} />

    <div style={{
      width: 44,
      height: 44,
      borderRadius: 14,
      background: color.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 14,
    }}>
      {icon}
    </div>

    <div style={{
      fontSize: 32,
      fontWeight: 800,
      color: '#1a1a2e',
      lineHeight: 1.1,
      marginBottom: 4,
      letterSpacing: '-0.5px',
    }}>
      {value}
    </div>
    <div style={{
      fontSize: 12,
      color: '#8b8fa8',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>
      {label}
    </div>
  </div>
);

export default AnalyticsPage;
